import { useState } from 'react';
import { alpha } from '@mui/material/styles';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Card, CardContent, TextField, Button, Alert,
  Chip, MenuItem, Stack, CircularProgress, Dialog,
  DialogContent, DialogActions,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  CalendarMonth as CalendarIcon,
  BeachAccess as VacationIcon, LocalHospital as SickIcon,
  ChildCare as ParentalIcon, EventBusy as CompensatoryIcon,
  FlightTakeoff as TravelIcon, EventNote as DefaultLeaveIcon,
  Add as AddIcon, HourglassEmpty as PendingIcon,
  CheckCircleOutline as ApprovedIcon, Cancel as RejectedIcon,
  EventAvailable as ScheduleIcon,
} from '@mui/icons-material';
import { TYPE } from '@/theme/typography';
import { STATUS_COLORS } from '@/theme/colorTokens';
import { employeesApi, leaveApi } from '@/api/endpoints/hr';
import { formatDate } from '@/utils/format';
import { useNotificationStore } from '@/stores/notificationStore';
import BrandedDialogTitle from '@/components/common/BrandedDialogTitle';

interface LeaveTabProps {
  emp: any;
}

export default function LeaveTab({ emp }: LeaveTabProps) {
  const qc = useQueryClient();
  const { showSuccess, showError } = useNotificationStore();

  const [leaveForm, setLeaveForm] = useState({ leave_type_id: '', start_date: '', end_date: '', reason: '', half_day: '' as '' | 'morning' | 'afternoon' });
  const [leaveFormOpen, setLeaveFormOpen] = useState(false);
  const [leaveCalcDays, setLeaveCalcDays] = useState(0);
  // Per-field errors raised by the server (422) so the user sees which field
  // failed instead of a generic toast. Cleared on every submit.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [cancelDialog, setCancelDialog] = useState<{ open: boolean; id: string; status: string }>({ open: false, id: '', status: '' });
  const [cancelReason, setCancelReason] = useState('');

  const { data: leaveTypes } = useQuery({
    queryKey: ['hr/leave-types'],
    queryFn: () => leaveApi.listTypes().then(r => r.data),
  });

  const { data: leaveBalances } = useQuery({
    queryKey: ['hr/leave-balances', emp?.id],
    queryFn: () => employeesApi.getLeaveBalances(emp!.id, new Date().getFullYear()).then(r => r.data),
    enabled: !!emp?.id,
  });

  const { data: leaveRequests, isLoading: leaveRequestsLoading, refetch: refetchRequests } = useQuery({
    queryKey: ['hr/leave-requests', emp?.id],
    queryFn: () => leaveApi.list({ employee_id: emp!.id, page: 1, per_page: 50 }).then(r => r.data),
    enabled: !!emp?.id,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  const leaveSubmitMutation = useMutation({
    mutationFn: (data: typeof leaveForm) => leaveApi.create(emp!.id, {
      leave_type_id: data.leave_type_id,
      start_date: data.start_date,
      end_date: data.half_day ? data.start_date : data.end_date,
      reason: data.reason,
      half_day: data.half_day || undefined,
    }),
    onMutate: () => {
      setFieldErrors({});
    },
    onSuccess: () => {
      showSuccess('Leave request submitted successfully');
      setLeaveFormOpen(false);
      setLeaveForm({ leave_type_id: '', start_date: '', end_date: '', reason: '', half_day: '' });
      setFieldErrors({});
      refetchRequests();
      qc.invalidateQueries({ queryKey: ['hr/leave-balances'] });
    },
    onError: (e: any) => {
      // FastAPI 422 bodies have `detail` as an array of {loc, msg, type}.
      // Split them into field-level inline errors so the user sees the
      // offending field instead of a generic "failed" toast.
      const detail = e?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const next: Record<string, string> = {};
        for (const item of detail) {
          const loc = Array.isArray(item?.loc) ? item.loc : [];
          const field = loc.length ? String(loc[loc.length - 1]) : '_form';
          next[field] = (next[field] ? `${next[field]}; ` : '') + (item?.msg || 'Invalid value');
        }
        setFieldErrors(next);
        if (next._form) showError(next._form);
      } else {
        showError(typeof detail === 'string' ? detail : 'Failed to submit leave request');
      }
    },
  });

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => leaveApi.cancel(id, reason),
    onSuccess: () => {
      showSuccess('Leave request cancelled');
      setCancelDialog({ open: false, id: '', status: '' });
      setCancelReason('');
      refetchRequests();
      qc.invalidateQueries({ queryKey: ['hr/leave-balances'] });
    },
    onError: (e: any) => showError(e.response?.data?.detail || 'Failed to cancel leave request'),
  });

  // `emp` is always truthy for an authenticated user — the my-profile endpoint
  // returns a *synthetic* profile (employee_ref === '') for users with no real
  // employee record. Gate on employee_ref so leave (which is employee-scoped)
  // shows clear guidance instead of submitting and failing server-side.
  if (!emp || !emp.employee_ref) {
    return (
      <Card sx={{ textAlign: 'center', py: 6 }}>
        <CalendarIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
        <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.semibold, mb: 1 }}>No Employee Record Found</Typography>
        <Typography sx={{ fontSize: TYPE.base, color: 'text.secondary', maxWidth: 400, mx: 'auto' }}>
          Leave management requires an employee record. Ask an administrator to create one in HR &rarr; Employees using your work email.
        </Typography>
      </Card>
    );
  }

  const totalRemaining = (leaveBalances || []).reduce((sum: number, b: any) => sum + (b.entitled_days - b.used_days), 0);
  const totalEntitled = (leaveBalances || []).reduce((sum: number, b: any) => sum + b.entitled_days, 0);
  const pendingCount = (leaveRequests?.items || []).filter((r: any) => r.status === 'pending').length;
  const nextApproved = (leaveRequests?.items || []).find((r: any) => r.status === 'approved' && new Date(r.start_date) >= new Date());

  return (
    <Box>
      {/* Summary Bar */}
      <Card sx={{ mb: 3, background: (theme: any) => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)` }}>
        <CardContent sx={{ p: '20px 24px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: TYPE.semibold }}>
                  Available Days
                </Typography>
                <Typography sx={{ fontSize: '2rem', fontWeight: TYPE.bold, lineHeight: 1.2, color: 'primary.main' }}>
                  {totalRemaining}
                  <Typography component="span" sx={{ fontSize: TYPE.base, fontWeight: TYPE.medium, color: 'text.secondary' }}> / {totalEntitled}</Typography>
                </Typography>
              </Box>
              {pendingCount > 0 && (
                <Box>
                  <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: TYPE.semibold }}>
                    Pending
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <PendingIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                    <Typography sx={{ fontSize: '2rem', fontWeight: TYPE.bold, lineHeight: 1.2, color: 'warning.main' }}>{pendingCount}</Typography>
                  </Stack>
                </Box>
              )}
              {nextApproved && (
                <Box>
                  <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: TYPE.semibold }}>
                    Next Leave
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <ScheduleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                    <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold, lineHeight: 1.8 }}>{formatDate(nextApproved.start_date)}</Typography>
                  </Stack>
                </Box>
              )}
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setLeaveFormOpen(true)}
              sx={{ borderRadius: '10px', px: 3, py: 1, fontWeight: TYPE.bold, fontSize: TYPE.md, textTransform: 'none', boxShadow: 2 }}
            >
              Apply for Leave
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Leave Balance Cards */}
      <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.bold, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
        Balances — {new Date().getFullYear()}
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {(leaveBalances || []).map((bal: any) => {
          const remaining = bal.entitled_days - bal.used_days;
          const pctUsed = bal.entitled_days > 0 ? (bal.used_days / bal.entitled_days) * 100 : 0;
          const statusColor = remaining >= bal.entitled_days * 0.5 ? STATUS_COLORS.success : remaining >= bal.entitled_days * 0.25 ? STATUS_COLORS.warning : STATUS_COLORS.error;
          const statusBg = remaining >= bal.entitled_days * 0.5 ? `${STATUS_COLORS.success}14` : remaining >= bal.entitled_days * 0.25 ? `${STATUS_COLORS.warning}14` : `${STATUS_COLORS.error}14`;
          const name = (bal.leave_type_name || 'Leave').toLowerCase();
          const LeaveIcon = name.includes('annual') || name.includes('vacation') ? VacationIcon
            : name.includes('sick') || name.includes('medical') ? SickIcon
            : name.includes('parent') || name.includes('matern') || name.includes('patern') ? ParentalIcon
            : name.includes('compens') || name.includes('lieu') ? CompensatoryIcon
            : name.includes('travel') || name.includes('study') ? TravelIcon
            : DefaultLeaveIcon;

          return (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={bal.leave_type_id}>
              <Card
                variant="outlined"
                sx={{
                  p: 0, borderRadius: '12px', borderColor: 'divider', overflow: 'hidden',
                  transition: 'box-shadow 0.2s, transform 0.15s',
                  '&:hover': { boxShadow: 3, transform: 'translateY(-2px)' },
                }}
              >
                <Box sx={{ height: 3, bgcolor: statusColor }} />
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box sx={{
                      width: 40, height: 40, borderRadius: '10px', bgcolor: statusBg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <LeaveIcon sx={{ fontSize: 22, color: statusColor }} />
                    </Box>
                    <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.bold }}>
                      {bal.leave_type_name || 'Leave'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                    <Box sx={{ position: 'relative', width: 64, height: 64, flexShrink: 0 }}>
                      <CircularProgress variant="determinate" value={100} size={64} thickness={5}
                        sx={{ color: 'action.hover', position: 'absolute', top: 0, left: 0 }} />
                      <CircularProgress variant="determinate" value={Math.min(pctUsed, 100)} size={64} thickness={5}
                        sx={{ color: statusColor, position: 'absolute', top: 0, left: 0, '& .MuiCircularProgress-circle': { strokeLinecap: 'round' } }} />
                      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ fontSize: TYPE.xl, fontWeight: TYPE.bold, color: statusColor }}>{remaining}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>Used</Typography>
                        <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.bold }}>{bal.used_days}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>Remaining</Typography>
                        <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.bold, color: statusColor }}>{remaining}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>Entitled</Typography>
                        <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.bold }}>{bal.entitled_days}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Leave Request History */}
      <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.bold, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
        Request History
      </Typography>
      {leaveRequestsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={32} /></Box>
      ) : (leaveRequests?.items || []).length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 5 }}>
          <VacationIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
          <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.semibold, mb: 0.5 }}>No leave requests yet</Typography>
          <Typography sx={{ fontSize: TYPE.base, color: 'text.secondary', mb: 2 }}>
            Your leave history will appear here once you apply.
          </Typography>
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setLeaveFormOpen(true)}
            sx={{ borderRadius: '8px', textTransform: 'none' }}>
            Apply for Leave
          </Button>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {(leaveRequests?.items || []).map((req: any) => {
            const statusConfig: Record<string, { color: string; bg: string; icon: React.ReactNode }> = {
              pending: { color: STATUS_COLORS.warning, bg: `${STATUS_COLORS.warning}14`, icon: <PendingIcon sx={{ fontSize: 18 }} /> },
              approved: { color: STATUS_COLORS.success, bg: `${STATUS_COLORS.success}14`, icon: <ApprovedIcon sx={{ fontSize: 18 }} /> },
              rejected: { color: STATUS_COLORS.error, bg: `${STATUS_COLORS.error}14`, icon: <RejectedIcon sx={{ fontSize: 18 }} /> },
              cancelled: { color: STATUS_COLORS.default, bg: `${STATUS_COLORS.default}14`, icon: <RejectedIcon sx={{ fontSize: 18 }} /> },
            };
            const sc = statusConfig[req.status] || statusConfig.pending;
            return (
              <Card
                key={req.id}
                variant="outlined"
                sx={{
                  borderRadius: '10px', borderLeft: `4px solid ${sc.color}`,
                  transition: 'box-shadow 0.15s', '&:hover': { boxShadow: 2 },
                }}
              >
                <CardContent sx={{ p: '14px 18px !important', '&:last-child': { pb: '14px !important' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: '8px', bgcolor: sc.bg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: sc.color, flexShrink: 0,
                    }}>
                      {sc.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 160 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                        <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.bold }}>
                          {req.leave_type_name || req.leave_type_id}
                        </Typography>
                        <Chip
                          label={req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                          size="small"
                          sx={{
                            height: 22, fontSize: TYPE.xs, fontWeight: TYPE.bold,
                            bgcolor: sc.bg, color: sc.color, border: 'none',
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      </Box>
                      <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary' }}>
                        {formatDate(req.start_date)}{req.start_date !== req.end_date ? ` — ${formatDate(req.end_date)}` : ''}
                        {' '}&middot;{' '}
                        <Typography component="span" sx={{ fontSize: TYPE.sm, fontWeight: TYPE.semibold, color: 'text.primary' }}>
                          {req.days} day{req.days !== 1 ? 's' : ''}{req.half_day ? ` (${req.half_day === 'morning' ? 'AM' : 'PM'})` : ''}
                        </Typography>
                      </Typography>
                      {req.reason && (
                        <Typography noWrap sx={{ fontSize: TYPE.xs, color: 'text.secondary', mt: 0.5, maxWidth: 400 }} title={req.reason}>
                          {req.reason}
                        </Typography>
                      )}
                      {req.review_notes && (
                        <Typography noWrap sx={{ fontSize: TYPE.xs, color: sc.color, mt: 0.25, fontStyle: 'italic', maxWidth: 400 }} title={req.review_notes}>
                          &ldquo;{req.review_notes}&rdquo;
                        </Typography>
                      )}
                    </Box>
                    {(req.status === 'pending' || req.status === 'approved') && (
                      <Button
                        size="small" color="error" variant="outlined"
                        onClick={() => setCancelDialog({ open: true, id: req.id, status: req.status })}
                        sx={{ minWidth: 'auto', fontSize: TYPE.xs, borderRadius: '8px', textTransform: 'none', px: 1.5 }}
                      >
                        Cancel
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* Cancel Leave Dialog */}
      <Dialog open={cancelDialog.open} onClose={() => setCancelDialog({ open: false, id: '', status: '' })} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}>
        <BrandedDialogTitle>Cancel Leave Request</BrandedDialogTitle>
        <DialogContent>
          {cancelDialog.status === 'approved' && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: '8px' }}>
              This request was already approved. Cancelling will refund the days to your balance.
            </Alert>
          )}
          <TextField
            label="Reason for cancellation"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            multiline rows={2} fullWidth
            placeholder="Optional reason..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCancelDialog({ open: false, id: '', status: '' })} sx={{ borderRadius: '8px', textTransform: 'none' }}>Back</Button>
          <Button
            variant="contained" color="error"
            disabled={cancelMutation.isPending}
            onClick={() => cancelMutation.mutate({ id: cancelDialog.id, reason: cancelReason || undefined })}
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            {cancelMutation.isPending ? 'Cancelling...' : 'Confirm Cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Apply for Leave Dialog */}
      <Dialog open={leaveFormOpen} onClose={() => setLeaveFormOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}>
        <BrandedDialogTitle>
          <CalendarIcon sx={{ color: 'primary.main' }} /> Apply for Leave
        </BrandedDialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField
            select label="Leave Type" value={leaveForm.leave_type_id}
            onChange={(e) => setLeaveForm(p => ({ ...p, leave_type_id: e.target.value, half_day: '' }))}
            fullWidth required
            error={!!fieldErrors.leave_type_id}
            helperText={fieldErrors.leave_type_id}
          >
            {(leaveTypes || []).map((lt: any) => (
              <MenuItem key={lt.id} value={lt.id}>
                {lt.name}{lt.is_paid === false ? ' (Unpaid)' : ''}
              </MenuItem>
            ))}
          </TextField>

          {leaveForm.leave_type_id && (leaveTypes || []).find((lt: any) => lt.id === leaveForm.leave_type_id)?.allow_half_day && (
            <TextField
              select label="Duration" value={leaveForm.half_day}
              onChange={(e) => {
                const val = e.target.value as '' | 'morning' | 'afternoon';
                setLeaveForm(p => ({ ...p, half_day: val }));
                if (val) setLeaveCalcDays(0.5);
              }}
              fullWidth size="small"
            >
              <MenuItem value="">Full day(s)</MenuItem>
              <MenuItem value="morning">Half day — Morning</MenuItem>
              <MenuItem value="afternoon">Half day — Afternoon</MenuItem>
            </TextField>
          )}

          <TextField
            label="Start Date" type="date" value={leaveForm.start_date}
            onChange={(e) => setLeaveForm(p => ({ ...p, start_date: e.target.value }))}
            InputLabelProps={{ shrink: true }} fullWidth required
            error={!!fieldErrors.start_date}
            helperText={fieldErrors.start_date}
          />
          {!leaveForm.half_day && (
            <TextField
              label="End Date" type="date" value={leaveForm.end_date}
              onChange={(e) => {
                const sd = new Date(leaveForm.start_date);
                const ed = new Date(e.target.value);
                const days = Math.floor((ed.getTime() - sd.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                setLeaveCalcDays(Math.max(0, days));
                setLeaveForm(p => ({ ...p, end_date: e.target.value }));
              }}
              InputLabelProps={{ shrink: true }} fullWidth required
              error={!!fieldErrors.end_date || (
                !!leaveForm.start_date
                && !!leaveForm.end_date
                && leaveForm.end_date < leaveForm.start_date
              )}
              helperText={
                fieldErrors.end_date
                || (
                  leaveForm.start_date
                  && leaveForm.end_date
                  && leaveForm.end_date < leaveForm.start_date
                    ? 'End date must be on or after start date.'
                    : ''
                )
              }
            />
          )}
          {leaveCalcDays > 0 && (
            <Box sx={{ bgcolor: 'action.hover', borderRadius: '8px', p: 1.5 }}>
              <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.semibold }}>
                {leaveCalcDays} day{leaveCalcDays !== 1 ? 's' : ''}
                {leaveForm.half_day ? ` (${leaveForm.half_day})` : ''}
              </Typography>
              <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>
                Final count excludes weekends and public holidays (calculated server-side).
              </Typography>
            </Box>
          )}
          <TextField
            label="Reason" value={leaveForm.reason}
            onChange={(e) => setLeaveForm(p => ({ ...p, reason: e.target.value }))}
            multiline rows={3} fullWidth required
            error={!!fieldErrors.reason}
            helperText={fieldErrors.reason}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLeaveFormOpen(false)} sx={{ borderRadius: '8px', textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              !leaveForm.leave_type_id || !leaveForm.start_date ||
              (!leaveForm.half_day && !leaveForm.end_date) ||
              (!leaveForm.half_day && leaveForm.end_date < leaveForm.start_date) ||
              !leaveForm.reason || leaveSubmitMutation.isPending
            }
            onClick={() => leaveSubmitMutation.mutate(leaveForm)}
            sx={{ borderRadius: '8px', textTransform: 'none', px: 3 }}
          >
            {leaveSubmitMutation.isPending ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
